import type { I_PlatformHandlers, T_SearchExistingCustomerResponse } from '@/types/Platforms';
import { E_CustomerLoyaltyError, type I_ErrorResponse } from '@/types/RegisteredCustomer';
import type {
  T_RedeemptionBodyParams,
  T_CouponRedeemptionBodyParams,
  T_ConfigureWalletPassURLBodyParams,
  T_ShopifyCart,
  T_RegisterCustomerBodyParams,
} from '@/types/Shopify';

export const PROXY_PREFIX = '/apps/enable';

const DISCOUNT_SESSION_KEY = 'enable_discount_codes';

// Intercepts Shopify "Buy Now" button clicks.
// "Buy Now" bypasses the main cart and creates a fresh checkout, so any loyalty discount would
// be lost. Instead we add the item to the main cart, apply the discount, and redirect to the
// standard checkout.
//
// IMPORTANT: preventDefault/stopImmediatePropagation must be called synchronously before any
// await, otherwise the async gap lets Shopify's web component fire and create its own checkout.
// When no discount is available we re-dispatch a flagged clone of the event so native Buy Now
// proceeds normally.

const bypassedEvents = new WeakSet<MouseEvent>();
let buyNowInterceptorSetup = false;

function setupBuyNowInterceptor(selector: string): void {
  if (buyNowInterceptorSetup) return;
  buyNowInterceptorSetup = true;

  document.addEventListener(
    'click',
    async (e: MouseEvent) => {
      // Skip events we re-dispatched ourselves (fallback path)
      if (bypassedEvents.has(e)) return;

      const target = e.target as HTMLElement;

      // Match the merchant-configured selector(s) for Buy Now / Dynamic Payment Buttons
      const checkoutBtn = target.closest(selector) as HTMLElement | null;

      if (!checkoutBtn) return;

      // Always intercept synchronously BEFORE any await so Shopify's web component handler
      // never fires during the async gap.
      e.preventDefault();
      e.stopImmediatePropagation();

      console.log('[Enable Widget] Buy Now intercepted');

      // --- Resolve discount codes ---
      // Priority 1: a code that was explicitly redeemed through the widget in this session
      let codesToApply = sessionStorage.getItem(DISCOUNT_SESSION_KEY);
      console.log('[Enable Widget] sessionStorage codes:', codesToApply);

      // Priority 2: auto-fetch the customer's loyalty data and collect any available codes
      if (!codesToApply) {
        console.log('[Enable Widget] fetching loyalty data for auto-redeem');
        try {
          const loyaltyRes = await fetch(`${PROXY_PREFIX}/customer-loyalty`, {
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          });

          if (loyaltyRes.ok) {
            const loyalty = (await loyaltyRes.json()) as {
              programs?: {
                loyaltyTier?: {
                  redemptions?: Array<{
                    discountCode: string;
                    remainingUses: number;
                    usageType: string;
                  }>;
                };
                flexiblePoints?: {
                  redemptions?: Array<{
                    discountCode: string;
                    remainingUses: number;
                    usageType: string;
                  }>;
                };
                coupons?: {
                  redemptions?: Array<{
                    discountCode: string;
                    remainingUses: number;
                    usageType: string;
                  }>;
                };
              };
            };

            const allRedemptions = [
              ...(loyalty.programs?.loyaltyTier?.redemptions ?? []),
              ...(loyalty.programs?.flexiblePoints?.redemptions ?? []),
              ...(loyalty.programs?.coupons?.redemptions ?? []),
            ];

            const availableCodes = [
              ...new Set(
                allRedemptions
                  .filter(
                    (r) => r.discountCode && (r.usageType === 'unlimited' || r.remainingUses > 0),
                  )
                  .map((r) => r.discountCode),
              ),
            ];

            console.log('[Enable Widget] available loyalty codes:', availableCodes);

            if (availableCodes.length > 0) {
              codesToApply = availableCodes.join(',');
            }
          }
        } catch (err) {
          console.error('[Enable Widget] failed to fetch loyalty data:', err);
        }
      }

      if (!codesToApply) {
        // No discount — let native Buy Now proceed by re-dispatching a flagged clone
        console.log('[Enable Widget] no discount codes — falling back to native Buy Now');
        const fallback = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          composed: true,
        });
        bypassedEvents.add(fallback);
        checkoutBtn.dispatchEvent(fallback);
        return;
      }

      console.log('[Enable Widget] applying codes:', codesToApply);

      const root = window.Shopify?.routes?.root || '/';

      // Get the variant to add to cart — prefer the product form, fall back to variant-params
      const form = checkoutBtn.closest('form') as HTMLFormElement | null;
      const acceleratedCheckout = checkoutBtn.closest(
        'shopify-accelerated-checkout',
      ) as HTMLElement | null;
      const variantParams = acceleratedCheckout?.getAttribute('variant-params');

      try {
        if (form) {
          await fetch(`${root}cart/add.js`, { method: 'POST', body: new FormData(form) });
        } else if (variantParams) {
          const variants = JSON.parse(variantParams) as Array<{ id: number }>;
          if (variants[0]?.id) {
            await fetch(`${root}cart/add.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: variants[0].id, quantity: 1 }),
            });
          }
        }

        await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: codesToApply }),
        });

        console.log('[Enable Widget] redirecting to checkout');
        window.location.href = `${root}checkout`;
      } catch (err) {
        console.error(
          '[Enable Widget] Buy Now interceptor failed — falling back to native Buy Now:',
          err,
        );
        const fallback = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          composed: true,
        });
        bypassedEvents.add(fallback);
        checkoutBtn.dispatchEvent(fallback);
      }
    },
    true, // capture phase — runs before Shopify's own handlers
  );
}

export const shopifyHandlers: I_PlatformHandlers = {
  setup: (buyNowButtonSelector) => {
    if (buyNowButtonSelector && typeof document !== 'undefined') {
      setupBuyNowInterceptor(buyNowButtonSelector);
    }
  },

  onDiscountRedemption: async ({ code }) => {
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: code ?? '' }), // NOTE: comma-separated for multiple discounts case but they needs to be allow combination with other discounts
      });

      if (!res.ok) throw new Error('Discount failed');

      // Persist the active codes so the Buy Now interceptor can reapply them
      if (code) {
        sessionStorage.setItem(DISCOUNT_SESSION_KEY, code);
        console.log('[Enable Widget] discount stored in sessionStorage:', code);
      } else {
        sessionStorage.removeItem(DISCOUNT_SESSION_KEY);
        console.log('[Enable Widget] discount cleared from sessionStorage');
      }
    } catch (err) {
      console.error('Shopify discount error:', err);
    }
  },

  getActiveCart: async () => {
    try {
      const res = await fetch('/cart.js');
      const cart = (await res.json()) as T_ShopifyCart;
      return cart;
    } catch (err) {
      console.error('Failed to fetch cart:', err);
      return null;
    }
  },
  searchExistingCustomer: async ({
    phoneNumber,
    countryCode,
  }: {
    phoneNumber: string;
    countryCode: string;
  }) => {
    const params = new URLSearchParams({
      phone: phoneNumber.trim(),
      countryCode: countryCode || '+974',
    });
    const response = await fetch(`${PROXY_PREFIX}/search-existing-customer?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });

    const data = await response.json();

    if (response.ok && data && typeof data.exists === 'boolean') {
      return data as T_SearchExistingCustomerResponse;
    }

    return { exists: false } as const;
  },
  fetchCustomerLoyalty: async () => {
    const response = await fetch(`${PROXY_PREFIX}/customer-loyalty`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });

    if (response.status === 404) {
      throw new Error(E_CustomerLoyaltyError.NOT_FOUND);
    }

    if (!response.ok) {
      const errorData = (await response.json()) as I_ErrorResponse;
      throw new Error(errorData.error || E_CustomerLoyaltyError.UNKNOWN);
    }

    return response.json();
  },
  registerCustomer: async (customerData: T_RegisterCustomerBodyParams) => {
    try {
      const response = await fetch(`${PROXY_PREFIX}/loyalty-registration/v2`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(customerData),
      });

      if (!response.ok) {
        const errorData = (await response.json()) as I_ErrorResponse;
        throw new Error(errorData.error || 'Failed to sync customer phone.');
      }
      return response.json();
    } catch (error) {
      console.error('Network error:', error);
      throw error;
    }
  },
  getShopLoyaltyConfig: async () => {
    const response = await fetch(`${PROXY_PREFIX}/shop/loyalty-config`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('error fetching ShopLoyaltyConfig');
    }

    return response.json();
  },
  configureWalletPassURL: async (registrationData: T_ConfigureWalletPassURLBodyParams) => {
    try {
      const response = await fetch(`${PROXY_PREFIX}/wallet-pass-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registrationData),
      });

      if (!response.ok) {
        const errorData = (await response.json()) as I_ErrorResponse;
        throw new Error(errorData.error || 'Failed to register customer');
      }
      return response.json();
    } catch (error) {
      console.error('Network error:', error);
      throw error;
    }
  },
  redeemFlexiblePoints: async (registrationData: T_RedeemptionBodyParams) => {
    try {
      const response = await fetch(`${PROXY_PREFIX}/flexible-points-benefit?operation=convert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registrationData),
      });

      if (!response.ok) {
        const errorData = (await response.json()) as I_ErrorResponse;
        throw new Error(errorData.error || 'Failed to redeem discount');
      }
      return response.json();
    } catch (error) {
      console.error('Network error:', error);
      throw error;
    }
  },
  redeemCoupon: async (redeemptionData: T_CouponRedeemptionBodyParams) => {
    try {
      const response = await fetch(`${PROXY_PREFIX}/coupon-benefit?operation=convert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(redeemptionData),
      });

      if (!response.ok) {
        const errorData = (await response.json()) as I_ErrorResponse;
        throw new Error(errorData.error || 'Failed to redeem coupon');
      }
      return response.json();
    } catch (error) {
      console.error('Network error:', error);
      throw error;
    }
  },
};
